/* eslint-disable @typescript-eslint/naming-convention,no-underscore-dangle */
import { HotTable } from '@handsontable/vue3';
import { asyncTimeout } from '@packages/toolbox/dist/utils/helpers';
import { registerAllModules } from 'handsontable/registry';
import { CellProperties, GridSettings } from 'handsontable/settings';
import { difference, omit, pick, sortBy, sum } from 'lodash';
import { useFormChild } from 'quasar';
import { ComputedRef, Ref, computed, nextTick, reactive, ref, toValue } from 'vue';

import useTableCatalogs from 'SharedComponents/Fields/composables/useTableCatalogs';
import { HandsontableCellType } from 'SharedDir/constants/field-types';
import {
  DynamicTableData,
  DynamicTableParams,
  FieldValue,
  FixedTableData,
  FixedTableParams,
  TableColumnType,
} from 'SharedDir/declarations/fields';
import TableColumn from 'SharedDir/service/fields/_Table/table-column';
import { headerHeightFixer } from 'SharedDir/service/fields/table.functions';
import CatalogEditor from 'SharedDir/utils/HandsontableCatalogsCellType/CatalogEditor';
import CatalogRenderer from 'SharedDir/utils/HandsontableCatalogsCellType/CatalogRenderer';

declare module 'handsontable/settings' {
  export interface GridSettings {
    /**
     * Await'ер для понимания того подгрузились каталоги или нет
     */
    catalogLoaderPromise?: Promise<void>;
  }
}

/**
 *
 * @param {ComputedRef<T> | T} params
 * @param {Ref<V>} innerValue
 * @param {() => void} emitValue
 * @param {Ref<UnwrapRef<typeof HotTable>>} HandsontableElement
 * @returns {{sumDictionary: ComputedRef<Record<string, string[]>>, hasSummedColumns: Ref<UnwrapRef<boolean>>, summaryRow: Ref<UnwrapRef<number | null>>, hasSummaryRow: Ref<UnwrapRef<boolean>>, summedColumns: Ref<UnwrapRef<Array<string>>>, processAutoSum: (cells: (Array<[number, string, number, number]> | null)) => Promise<void>, nonSummableTechnicalColumns: Ref<UnwrapRef<string[]>>, processInitAutosum: () => Promise<void>, nonSummableColumns: Ref<UnwrapRef<string[]>>, checkSummaryColumn: (_params: (ComputedRef<T> | T)) => void, overrideCellsReadonly: (row: number, col: number) => Partial<{readOnly: true, editor: false}>, forceHotRefresh: () => Promise<void>, invertedSumDictionary: ComputedRef<Record<string, Array<any>>>}}
 */
export const useBaseTable = <
  T extends FixedTableParams | DynamicTableParams,
  V extends FieldValue<FixedTableData | DynamicTableData>
>(
  params: T | ComputedRef<T>,
  innerValue: Ref<V>,
  emitValue: () => void,
  HandsontableElement: Ref<typeof HotTable>
) => {
  if (process.env.CLIENT) {
    // По какой-то причине любые попытки переназначить значение из `process.env.CLIENT`,
    // или `process.env.SERVER` в переменную что бы потом можно было использовать ее.
    // А не каждый раз дергать процесс.
    // Не увенчались успехом так что пока используем обращение к инстансу процесса.
    // В данном случае регистарция модулей хэндса должна происходить
    // на клиенте потому что используется `navigator`
    registerAllModules();
  }
  /**
   * Флаг наличия столбцов-агрегаторов
   *
   * @type {boolean}
   */
  const hasSummedColumns = ref<boolean>(false);

  /**
   * Флаг наличия итоговой строки
   *
   * @type {boolean}
   */
  const hasSummaryRow = ref<boolean>(false);

  /**
   * Slug'и столбцов-агрегаторов
   *
   * Значения этих столбцов будут вычисляться как сумма
   * значений ячеек из подмножества каждой строки.
   *
   * @type {Array<string>}
   */
  const summedColumns = ref<Array<string>>([]);

  /**
   * Индекс итоговой строки
   *
   * @type {(null | number)}
   */
  const summaryRow = ref<null | number>(null);

  /**
   * Список slug'ов не суммируемых колонок
   *
   * @type {Array<string>}
   */
  const nonSummableColumns = ref<string[]>([]);

  /**
   * Список slug'ов не суммируемых **технических** колонок
   *
   * по своей сути это свойства объекта начинающиеся с _$_ и служащие исключительно для
   * указания "индекса" строки, или "названия"
   *
   * @type {Array<string>}
   */
  const nonSummableTechnicalColumns = ref<string[]>([]);

  /**
   * Индикатор ошибок при валидации столбцов таблицы
   */
  const hasErrors = ref(false);

  const settings = reactive<GridSettings>({
    colHeaders: true,
    height: 'auto',
    // todo: прокинуть ключ через службу или глобальную настройку приложения где эта компонента будет использоваться
    licenseKey: 'non-commercial-and-evaluation',
    nestedHeaders: [[]] as any,
    columnSummary: [],
    rowHeaders: true,
    data: [] as any,
    columns: [] as any,
    modifyColWidth(width, col) {
      if (width > 200) {
        return 200;
      }
      return width;
    },
    ...headerHeightFixer,
  });

  /**
   * Словарь для определения какие столбцы-агрегаторы от каких зависят.
   *
   * ```
   *    {
   *      'slug столбца-агрегатора': [массив slug'ов столбцов-источников],
   *      ....
   *    }
   * ```
   *
   * @returns {Record<string, Array<string>>}
   */
  const sumDictionary = computed<Record<string, string[]>>(() => {
    const entries = toValue(params)
      .cols.columns.filter(({ isSummary }) => isSummary)
      .map((column) => {
        if (Array.isArray(column.summedColumns)) {
          return [column.slug, column.summedColumns];
        }
        // Все что далее направлено на поддержку обратной совместимости,
        // потому что в предыдущей версии столбцы-агрегаторы
        // не поддерживали выбор столбцов-источников.
        // Источниками считались все столбцы кроме несуммируемых.
        const except = [
          column.slug,
          ...toValue(nonSummableTechnicalColumns),
          ...toValue(nonSummableColumns),
        ];
        const allExceptNonSummable = toValue(params)
          .cols.columns.map(({ slug }) => slug)
          .filter((slug) => !except.includes(slug));
        return [column.slug, allExceptNonSummable];
      });
    return Object.fromEntries(entries);
  });

  /**
   * Словарь для определения какие столбцы являются источниками столбцов-агрегаторов.
   *
   * ```
   *    {
   *      'slug столбца-источников': [массив slug'ов столбцов-агрегаторов],
   *      ....
   *    }
   * ```
   *
   * @returns {Record<string, Array<string>>}
   */
  const invertedSumDictionary = computed(() => {
    const dictionary: Record<string, Array<any>> = {};
    Object.entries(toValue(sumDictionary)).forEach(([sumSlug, sumSubset]) => {
      sumSubset.forEach((subSetItem) => {
        if (Array.isArray(dictionary[subSetItem])) {
          dictionary[subSetItem].push(sumSlug);
        } else {
          dictionary[subSetItem] = [sumSlug];
        }
      });
    });
    return dictionary;
  });

  /**
   * Проверяем указан ли какой-либо столбец как агрегатор
   *
   * если да, то выставляем соответствующий флаг и запоминаем slug'и этих столбцов.
   *
   * @param _params
   */
  const checkSummaryColumn = (_params: T | ComputedRef<T>) => {
    const _summedColumns = toValue(_params)
      .cols.columns.filter(({ isSummary }) => isSummary)
      .map(({ slug }) => slug);
    if (_summedColumns.length > 0) {
      hasSummedColumns.value = true;
      summedColumns.value = _summedColumns;
    } else {
      hasSummedColumns.value = false;
      summedColumns.value = [];
    }
  };

  /**
   * Подтянуть актуальный `innerValue` в уже созданный Handsontable.
   *
   * `updateSettings({ data })` ставит `forceFullRender` и на время блокирует редактор —
   * грид вспыхивает на каждую правку. `getSourceData()` при непустой таблице
   * возвращает проекцию, не исходный массив, поэтому ссылку берём из `getSettings().data`.
   * `countSourceRows()` уже видит splice того же массива, а `countRows()` — нет,
   * пока index mapper не подогнан через `updateData`.
   */
  const forceHotRefresh = async () => {
    console.debug('Force update settings');
    await nextTick();
    try {
      const { hotInstance } = HandsontableElement.value;
      const data = innerValue.value.value;
      const sameSource = hotInstance.getSettings().data === data;
      const gridKnowsEveryRow = hotInstance.countRows() === data.length;
      if (sameSource && gridKnowsEveryRow) {
        hotInstance.render();
        return;
      }
      if (typeof hotInstance.updateData === 'function') {
        hotInstance.updateData(data);
        return;
      }
      hotInstance.updateSettings({ data });
    } catch (error) {
      if (error instanceof TypeError) {
        console.error('Для корректной работы компоненты необходима ссылка на нее в $refs.');
      } else {
        throw error;
      }
    }
  };

  /**
   * Метод нужен только для передачи в качестве
   * свойства _cells_ в настройки _settings_
   * для лочинга ячеек итоговой строки.
   *
   * @param {number} row
   * @param col
   * @returns {Partial<{
   *     readOnly: true;
   *     editor: false
   *   }>}
   */
  const overrideCellsReadonly = (
    row: number,
    col: number
  ): Partial<{ readOnly: true; editor: false }> => {
    if (row === toValue(summaryRow)) {
      return {
        readOnly: true,
        editor: false,
      };
    }

    const { readOnly, editor } = (settings.columns || {})[col] || {};
    return { readOnly, editor };
  };

  /**
   * Обновляем столбцы-агрегаторы, путем применения к ним _дельты_
   *
   * _дельта_ - разница между предыдущим и новым значением измененной ячейки,
   * которая является источником для агрегатора.
   *
   * @param {number} rowIndex
   * @param {string} columnSlug - идентификатор изменяемого столбца
   * @param {number} diff -_дельта_
   */
  const updateSummedColumns = (rowIndex: number, columnSlug: string, diff: number) => {
    if (
      Array.isArray(summedColumns.value) &&
      summedColumns.value.length > 0 &&
      invertedSumDictionary.value[columnSlug]
    ) {
      invertedSumDictionary.value[columnSlug].forEach((summedColumn) => {
        (innerValue.value.value[rowIndex][summedColumn] as number) += diff;
      });
    }
  };

  /**
   * Обновляем строку-агрегатора, путем применения к ней _дельты_
   *
   * _дельта_ - разница между предыдущим и новым значением измененной ячейки,
   * которая является источником для агрегатора.
   *
   * @param {string} columnSlug - идентификатор изменяемого столбца
   * @param {number} diff -_дельта_
   * @memberof BaseTableField
   */
  const updateSummaryRow = (columnSlug: string, diff: number) => {
    if (typeof summaryRow.value === 'number' && !nonSummableColumns.value.includes(columnSlug)) {
      const summaryDataRow = innerValue.value.value[summaryRow.value];
      (summaryDataRow[columnSlug] as number) += diff;
      if (Array.isArray(invertedSumDictionary[columnSlug])) {
        invertedSumDictionary.value[columnSlug].forEach((summedColumn) => {
          (summaryDataRow[summedColumn] as number) += diff;
        });
      }
    }
  };

  /**
   * Метод, который служит исключительно для передачи в качестве
   * значения `afterChange` в настройках `HotTable`
   *
   * Служит для обновления значения итоговых строки и столбца,
   * при наличии соответствующих флагов.
   *
   * Сначала будет обновлено значение итогового столбца,
   * а затем строки
   *
   * Если есть итоговый столбец или строка, их ячейки уже лежат в том же массиве —
   * достаточно `render`. `updateSettings({ data })` здесь мигает гридом.
   * Значение наружу продьюсится в любом случае.
   *
   * @param {(null | Array<[number, string, number, number]>)} cells
   * @returns {void}
   */
  const processAutoSum = async (cells: null | Array<[number, string, number, number]>) => {
    if (cells === null) return;
    if (hasSummedColumns.value) {
      cells.forEach(([rowIndex, columnSlug, oldValue, newValue]) => {
        if (
          !nonSummableColumns.value.includes(columnSlug) &&
          hasSummedColumns.value &&
          oldValue !== newValue
        ) {
          updateSummedColumns(rowIndex, columnSlug, newValue - oldValue);
        }
      });
    }
    if (hasSummaryRow.value) {
      cells.forEach(([rowIndex, columnSlug, oldValue, newValue]) => {
        if (rowIndex !== summaryRow.value && oldValue !== newValue) {
          updateSummaryRow(columnSlug, newValue - oldValue);
        }
      });
    }
    // Правка ячейки уже на экране. Итоги записаны в те же объекты — без render соседние ячейки останутся старыми.
    if (hasSummedColumns.value || hasSummaryRow.value) {
      HandsontableElement.value?.hotInstance?.render();
    }
    hasErrors.value = false;
    emitValue();
  };

  /**
   * Первоначальная актуализация итоговой строки и столбца,
   * при наличии соответствующих флагов.
   *
   * При их наличии в итоговой строке или столбце,
   * будут заменены актуальными значениями.
   *
   * Сначала считается итоговый столбец, затем строка
   *
   */
  const processInitAutosum = async () => {
    /**
     * Опытным путем выяснилось что задержка просто необходима.
     * Скорее всего это связано с тем, что этот метод вызывается при парсинге параметров поля,
     * а не в момент монтирования, потому что нам для брендбука нужна их реактивность.
     */
    await asyncTimeout(300);
    if (hasSummedColumns.value && summedColumns.value.length > 0) {
      console.debug(summedColumns.value, nonSummableColumns.value);
      (innerValue.value.value as Array<any>).forEach((dataRow, rowIndex) => {
        const refinedRow = omit(dataRow, [
          ...nonSummableColumns.value,
          ...nonSummableTechnicalColumns.value,
          ...summedColumns.value,
        ]);
        summedColumns.value.forEach((sumSlug) => {
          const sumSubset = pick(refinedRow, sumDictionary.value[sumSlug]);
          innerValue.value.value[rowIndex][sumSlug] = sum(Object.values(sumSubset));
        });
      });
    }

    if (hasSummaryRow.value && summaryRow.value !== null) {
      const columnSlugs = difference(
        toValue(params).cols.columns.map(({ slug }) => slug),
        nonSummableColumns.value
      );
      const total = Object.fromEntries(columnSlugs.map((slug) => [slug, 0]));
      (innerValue.value.value as Array<any>).forEach((dataRow, rowIndex) => {
        if (rowIndex === summaryRow.value) return;
        columnSlugs.forEach((slug) => {
          total[slug] += dataRow[slug] as number;
        });
      });
      const totalRow = innerValue.value.value[summaryRow.value];
      if (totalRow) {
        columnSlugs.forEach((slug) => {
          totalRow[slug] = total[slug];
        });
      }
    }

    await forceHotRefresh();
  };

  const { catalogsLoading, catalogsOptions, loadCatalogs } = useTableCatalogs();

  function fetchCatalogValues(columns: TableColumn[]) {
    settings.catalogLoaderPromise = loadCatalogs(columns);
    return settings.catalogLoaderPromise;
  }

  function makeCatalogColumn(column: TableColumnType) {
    return {
      data: column.slug,
      // По неведомой причине регистрация кастомного типа через `registerCellType`
      // не работает, так что указываем редактор и рендерер прямо в настройках ячейки
      renderer: CatalogRenderer,
      editor: CatalogEditor,
      selectOptions: (row, col, prop) => {
        // Вообще можно и массив сюда передавать, однако, в момент генерации этого объекта
        // наши значения каталогов могут и не подгрузиться, так что замыканием будет более надежно.
        return catalogsOptions.value[column.catalogSlug] || [];
      },
    };
  }

  /**
   * Формируем массив с колонками для компоненты HotTable
   * @param columns
   * @returns
   */
  function getColumns(columns: Array<TableColumnType>) {
    const sorted = sortBy(columns, 'order');
    return sorted.map((c) => {
      const column = new TableColumn(c);
      let readonly = {};
      if (column.isSummary) {
        readonly = {
          editor: false,
          readOnly: true,
        };
      }
      if (column.type === HandsontableCellType.catalog) {
        return makeCatalogColumn(column);
      }

      const cellProperties: CellProperties = {
        data: column.slug,
        allowInvalid: false,
        ...readonly,
      };

      const isNumeric = [
        HandsontableCellType.numeric,
        HandsontableCellType.catalogRecordAmount,
      ].includes(column.type);
      if (isNumeric) {
        cellProperties.editor = 'empty-to-null';
        cellProperties.validator = 'numeric';
      }

      if (column.type === HandsontableCellType.catalogRecordAmount) {
        cellProperties.catalogColumnSlug = column.catalogColumnSlug;
        cellProperties.validator = 'catalog-record-amount';
      }

      return cellProperties;
    });
  }

  function validate(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const columns: number[] = (
        HandsontableElement.value.hotInstance.getSettings().columns as Array<unknown>
      ).map((_, index) => index);
      HandsontableElement.value.hotInstance.validateColumns(columns, resolve);
    });
  }

  useFormChild({
    validate: async () => {
      const valid = await validate();
      hasErrors.value = !valid;
      return valid;
    },
    resetValidation() {
      hasErrors.value = false;
    },
  });
  return {
    settings,
    hasSummedColumns,
    hasSummaryRow,
    summedColumns,
    summaryRow,
    nonSummableColumns,
    nonSummableTechnicalColumns,
    sumDictionary,
    invertedSumDictionary,
    checkSummaryColumn,
    forceHotRefresh,
    overrideCellsReadonly,
    processAutoSum,
    processInitAutosum,
    catalogsOptions,
    catalogsLoading,
    fetchCatalogValues,
    getColumns,
    makeCatalogColumn,
    validate,
    hasErrors,
  };
};
